// src/host/workplace/services/BreakService.ts
import { ApiService } from '@/shared/services/api.service';
import { BreakDetails, useBreakStore } from '../stores/BreakStore';
import { WorkplaceEventRouter } from '../events/WorkplaceEventRouter';
import { useWorkplaceStore } from '@host/stores/workplace.store';
import { AppSDK as MeetingApp } from '@apps/meeting/AppController';
import { Logger } from '../../../shared/services/logger.service';
import { UserBreakStartEvent, UserBreakEndEvent } from 'nexgen-types';
import { GlobalEventService } from '@host/services/event.service';
import { getDeviceId, DEVICE_KIND } from '@host/services/device-id.service';
import { isActivityTrackingEnabled } from '@host/services/activity-tracking-flag.util';
import { useSessionStore } from '@host/stores/session.store';
import { RoomService } from '@/shared/services/roomServices';

// ---- API Endpoints ----
const API_ENDPOINTS = {
  BREAK_START: {
    endpoint: '/break',
    verb: 'POST',
    service: 'app' as const,
  },
  BREAK_UPDATE: {
    endpoint: '/break',
    verb: 'PATCH',
    service: 'app' as const,
  },
  BREAK_STOP: {
    endpoint: '/break/stop',
    verb: 'POST',
    service: 'app' as const,
  },
  BREAK_DETAILS: {
    endpoint: '/break',
    verb: 'GET',
    service: 'app' as const,
  },
} as const;

export class BreakService {
  private static instance: BreakService;

  // Room the user was in when they took a break — captured right before
  // leaveMeeting() so executeMeetingEndActions() can rejoin the same room.
  // null whenever they weren't in a meeting when the break started.
  private roomPidBeforeBreak: string | null = null;

  static getInstance(): BreakService {
    if (!BreakService.instance) {
      BreakService.instance = new BreakService();
    }
    return BreakService.instance;
  }

  /**
   * 🚀 Quick start a break with defaults - primary method for UI components
   */
  async quickStartBreak(): Promise<{
    success: boolean;
    wasAlreadyActive: boolean;
    error?: string;
  }> {
    const store = useBreakStore.getState();
    const workplaceStore = useWorkplaceStore.getState();

    try {
      // Check if user is in their home room
      if (!workplaceStore.canTakeBreakFromCurrentRoom()) {
        Logger.debug('[BreakService] Cannot take break - user not in home room');
        return {
          success: false,
          wasAlreadyActive: false,
          error: 'You can only take a break from your home room',
        };
      }

      // Check if break is already active
      if (store.breakDetails) {
        Logger.debug('[BreakService] Break already active - no action needed');
        return {
          success: true,
          wasAlreadyActive: true,
        };
      }

      Logger.debug('[BreakService] Starting quick break with defaults...');

      // Start break with sensible defaults
      await this.startBreak({
        duration: 10, // 10 minutes default
        type: '', // User will select in dialog
        reason: '', // User will fill in dialog
      });

      Logger.debug('[BreakService] ✅ Quick break started successfully');

      return {
        success: true,
        wasAlreadyActive: false,
      };
    } catch (error) {
      console.error('[BreakService] ❌ Quick break failed:', error);

      const errorMessage = error instanceof Error ? error.message : 'Failed to start break';

      return {
        success: false,
        wasAlreadyActive: false,
        error: errorMessage,
      };
    }
  }

  async startBreak(breakData: {
    duration: number;
    type: string;
    reason: string;
  }): Promise<BreakDetails | null> {
    const store = useBreakStore.getState();
    const wpStore = useWorkplaceStore.getState();

    try {
      store.setLoading(true);
      store.clearError();

      Logger.debug('🚀 [BreakService] startBreak called with:', breakData);

      // 🎯 OPTIMISTIC STATE UPDATES
      const optimisticBreakDetails: BreakDetails = {
        duration: breakData.duration,
        type: breakData.type,
        reason: breakData.reason,
        startedAt: new Date(),
        return_estimate: this.calculateReturnTime(breakData.duration),
      };

      store.updateBreakDetails(optimisticBreakDetails);
      wpStore.setUserStatus('break');

      // 🚀 IMMEDIATE EVENT EMISSION - Don't wait for API!
      const userContext = wpStore.getUserContext();
      const optimisticMessage = `${breakData.reason || 'Taking a break'}. Expected return [[${optimisticBreakDetails.return_estimate.toISOString()}]]`;

      WorkplaceEventRouter.emitUserStatusChanged({
        status: 'break',
        message: optimisticMessage,
        ...userContext,
      });
      this.emitBreakTransition('start');

      Logger.debug('[BreakService] ⚡ Optimistic: Break status emitted immediately');

      // 🎬 Meeting actions (non-blocking)
      try {
        await this.executeMeetingStartActions();
      } catch (meetingError) {
        console.warn('[BreakService] ⚠️ Meeting actions failed:', meetingError);
      }

      // 📡 API Call (in background)
      try {
        const config = API_ENDPOINTS.BREAK_START;
        const response = await ApiService.request({
          method: config.verb,
          endpoint: config.endpoint,
          data: {
            break_type: breakData.type,
            break_message: breakData.reason,
            duration_minutes: breakData.duration,
          },
          service: config.service,
        });

        if (response) {
          // 🔄 UPDATE with server response (refinement, not initial state)
          const serverBreakDetails: BreakDetails = {
            duration: response.duration_minutes,
            type: response.break_type,
            reason: response.break_message,
            startedAt: new Date(response.start_time),
            return_estimate: new Date(response.return_estimate),
          };

          store.updateBreakDetails(serverBreakDetails);

          // 📡 REFINED EVENT - Update with accurate server data
          const refinedMessage = `${response.break_message || 'Taking a break'}. Expected return [[${response.return_estimate}]]`;

          WorkplaceEventRouter.emitUserStatusChanged({
            status: 'break',
            message: refinedMessage,
            ...userContext,
          });

          Logger.debug('[BreakService] 📡 Server: Break confirmed and refined');
          return serverBreakDetails;
        }

        return optimisticBreakDetails;
      } catch (apiError) {
        console.error('[BreakService] ❌ API failed - reverting optimistic changes:', apiError);

        // 🔄 REVERT OPTIMISTIC CHANGES
        store.updateBreakDetails(null);
        wpStore.setUserStatus('active');

        // 📡 REVERT EVENT - Tell everyone the break failed
        WorkplaceEventRouter.emitUserStatusChanged({
          status: 'active',
          message: 'Break cancelled due to system error',
          ...userContext,
        });
        this.emitBreakTransition('end');

        // 🎬 Revert meeting actions
        await this.revertMeetingStartActions();

        throw apiError; // Re-throw so UI can handle
      }
    } catch (error) {
      console.error('[BreakService] ❌ Break start failed:', error);
      store.setError('Failed to start break');
      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  async updateBreak(updates: { duration?: number; type?: string; reason?: string }): Promise<void> {
    const store = useBreakStore.getState();
    const currentBreak = store.breakDetails;

    if (!currentBreak) {
      throw new Error('No active break to update');
    }

    try {
      store.setLoading(true);

      // 🎯 Optimistic UI
      const optimisticUpdate: BreakDetails = {
        ...currentBreak,
        ...updates,
        return_estimate: updates.duration
          ? this.calculateReturnTime(updates.duration)
          : currentBreak.return_estimate,
      };

      store.updateBreakDetails(optimisticUpdate);
      Logger.debug('[BreakService] 🎯 Optimistic UI: Break updated immediately');

      // 📡 API Call
      const config = API_ENDPOINTS.BREAK_UPDATE;
      const response = await ApiService.request({
        method: config.verb,
        endpoint: config.endpoint,
        data: {
          break_type: updates.type || currentBreak.type,
          break_message: updates.reason || currentBreak.reason,
          duration_minutes: updates.duration || currentBreak.duration,
        },
        service: config.service,
      });

      if (response) {
        const updatedBreakDetails: BreakDetails = {
          duration: response.duration_minutes,
          type: response.break_type,
          reason: response.break_message,
          startedAt: new Date(response.start_time),
          return_estimate: new Date(response.return_estimate),
        };

        store.updateBreakDetails(updatedBreakDetails);
        Logger.debug('[BreakService] 📡 Server response: Break update confirmed');

        // Keep the return time shown on the avatar in sync with the server
        await this.updateBreakOfficeMessage(
          updatedBreakDetails.reason,
          updatedBreakDetails.return_estimate
        );

        // 📢 Emit Update Event (still on_break, but with new metadata)
        WorkplaceEventRouter.emitUserStatusChanged({
          user_pid: response.user_pid,
          status: 'break',
        });
      }
    } catch (error) {
      console.error('[BreakService] ❌ Failed to update break:', error);
      store.setError('Failed to update break');
      // Revert to previous state
      store.updateBreakDetails(currentBreak);
      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  async endBreak(): Promise<void> {
    const store = useBreakStore.getState();
    const wpStore = useWorkplaceStore.getState();
    const currentBreak = store.breakDetails;

    try {
      store.setLoading(true);

      // 🎯 Optimistic UI
      store.updateBreakDetails(null);
      wpStore.setUserStatus('active');
      this.emitBreakTransition('end');
      Logger.debug('[BreakService] 🎯 Optimistic UI: Break ended immediately');

      // 🎬 Orchestrate meeting restoration through controller
      try {
        await this.executeMeetingEndActions();
        Logger.debug('[BreakService] 🎬 Meeting restoration completed');
      } catch (meetingError) {
        console.warn('[BreakService] ⚠️ Meeting restoration failed:', meetingError);
      }

      // 📡 API Call
      const config = API_ENDPOINTS.BREAK_STOP;
      const response = await ApiService.post(config.endpoint, {}, { service: config.service });

      if (response) {
        const userContext = wpStore.getUserContext();
        WorkplaceEventRouter.emitUserStatusChanged({
          status: 'active',
          message: '',
          ...userContext,
        });
      }
    } catch (error) {
      console.error('[BreakService] ❌ Failed to end break:', error);
      store.setError('Failed to end break');

      store.updateBreakDetails(currentBreak);
      // Rollback the optimistic break.end emitted above — the break is still
      // open as far as the activity-metrics pipeline should be concerned.
      if (currentBreak) {
        this.emitBreakTransition('start');
        await this.revertMeetingEndActions();
      }

      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  /**
   * Publish the break reason + return time as the home room's office message,
   * which is what other users see on the avatar. Always pass the server's
   * return_estimate so it matches the break record, not the local clock.
   */
  async updateBreakOfficeMessage(reason: string, returnEstimate?: Date): Promise<void> {
    if (!returnEstimate || isNaN(returnEstimate.getTime())) return;

    const message = `${reason || 'Taking a quick break'} - Return time: ${returnEstimate.toISOString()}`;
    await RoomService.updateOfficeData(message);
  }

  async getBreakDetails(options?: { emitStatusChanged?: boolean }): Promise<BreakDetails | null> {
    const store = useBreakStore.getState();
    const wpStore = useWorkplaceStore.getState();
    const shouldEmit = options?.emitStatusChanged !== false;

    try {
      store.setLoading(true);

      const config = API_ENDPOINTS.BREAK_DETAILS;
      const response = await ApiService.get(config.endpoint, { service: config.service });

      if (response) {
        const breakDetails: BreakDetails = {
          duration: response.duration_minutes,
          type: response.break_type,
          reason: response.break_message,
          startedAt: new Date(response.start_time),
          return_estimate: new Date(response.return_estimate),
        };

        store.updateBreakDetails(breakDetails);

        // Sync workplace store status so avatar reflects break state (e.g. after page refresh)
        wpStore.setUserStatus('break');

        if (shouldEmit) {
          const userContext = wpStore.getUserContext();
          WorkplaceEventRouter.emitUserStatusChanged({
            status: 'break',
            message: response.break_message || 'On break',
            ...userContext,
          });
        }

        Logger.debug('[BreakService] 📡 Loaded existing break details');
        return breakDetails;
      }

      return null;
    } catch (error) {
      console.error('[BreakService] ❌ Failed to get break details:', error);
      store.setError('Failed to load break details');
      return null;
    } finally {
      store.setLoading(false);
    }
  }

  // ========================================
  // PRIVATE HELPER METHODS - Meeting Orchestration
  // ========================================

  /**
   * Execute all meeting actions when break starts — leaves the current
   * meeting (if any) so the activity-metrics pipeline's meeting dimension
   * actually closes for the break's duration, instead of staying open in
   * the background (which was double-counting break time as meeting/active
   * time — see NGE-839 follow-up). The room is remembered so
   * executeMeetingEndActions() can rejoin it once the break ends.
   */
  private async executeMeetingStartActions(): Promise<void> {
    if (MeetingApp.isInMeeting()) {
      this.roomPidBeforeBreak = MeetingApp.getCurrentMeetingRoomPid() || null;
      Logger.debug('[BreakService] 📞 User in meeting - leaving...', this.roomPidBeforeBreak);
      await MeetingApp.leaveMeeting();
    } else {
      this.roomPidBeforeBreak = null;
    }
  }

  /**
   * Execute all meeting actions when break ends — rejoins whichever meeting
   * room the user was in before the break started, if any.
   */
  private async executeMeetingEndActions(): Promise<void> {
    // Captured locally and cleared BEFORE attempting the join (not after it
    // succeeds) — if joinMeeting() throws, a stale pid must not linger on
    // the singleton and trigger an unintended rejoin on a later break.
    const roomPid = this.roomPidBeforeBreak;
    this.roomPidBeforeBreak = null;

    if (roomPid) {
      Logger.debug('[BreakService] 📞 Rejoining meeting after break...', roomPid);
      await MeetingApp.joinMeeting(roomPid);
    }
  }

  /**
   * Revert meeting actions if break start fails (after already leaving the
   * meeting) — rejoin the same room so the failed break doesn't leave the
   * user disconnected from a call they never actually meant to leave.
   */
  private async revertMeetingStartActions(): Promise<void> {
    try {
      if (this.roomPidBeforeBreak) {
        await MeetingApp.joinMeeting(this.roomPidBeforeBreak);
        this.roomPidBeforeBreak = null;
      }
      Logger.debug('[BreakService] 🔄 Meeting start actions reverted');
    } catch (revertError) {
      console.warn('[BreakService] ⚠️ Failed to revert meeting start actions:', revertError);
    }
  }

  /**
   * Revert meeting actions if break end fails (after already rejoining the
   * meeting) — the break.end emission gets rolled back to break.start by
   * the caller, so leave the meeting again to match that "still on break" state.
   */
  private async revertMeetingEndActions(): Promise<void> {
    try {
      if (MeetingApp.isInMeeting()) {
        this.roomPidBeforeBreak = MeetingApp.getCurrentMeetingRoomPid() || null;
        await MeetingApp.leaveMeeting();
      }
      Logger.debug('[BreakService] 🔄 Meeting end actions reverted');
    } catch (revertError) {
      console.warn('[BreakService] ⚠️ Failed to revert meeting end actions:', revertError);
    }
  }

  /**
   * NGE-836/839: dedicated break.start/break.end transitions — the
   * status.changed(break/active) events emitted elsewhere in this file drive
   * presence UI, but the activity-metrics rollup's break dimension only
   * recognizes these two event types (interval-merge.ts BREAK_OPEN/BREAK_CLOSE).
   * Without this, break_seconds/breaks_count would always stay zero.
   */
  private emitBreakTransition(transition: 'start' | 'end'): void {
    if (!isActivityTrackingEnabled()) return;

    try {
      const user_pid = useWorkplaceStore.getState().user?.pid;
      if (!user_pid) return;

      const org_pid = useWorkplaceStore.getState().org?.pid;
      const session_id = useSessionStore.getState().sessionId ?? undefined;

      const payload = {
        user_pid,
        org_pid,
        session_id,
        device_id: getDeviceId(),
        device_kind: DEVICE_KIND,
      };

      if (transition === 'start') {
        GlobalEventService.emitTyped(new UserBreakStartEvent(payload), true);
      } else {
        GlobalEventService.emitTyped(new UserBreakEndEvent(payload), true);
      }
    } catch (error) {
      console.error('[BreakService] Failed to emit break activity transition:', error);
    }
  }

  /**
   * Calculate return time based on duration
   */
  private calculateReturnTime(durationMinutes: number): Date {
    const now = new Date();
    now.setMinutes(now.getMinutes() + durationMinutes);
    return now;
  }
}
